/**
 * 🪟🫁 portedLoader — the pure half of the ported-space arrival fixes (Nina 09-24).
 *
 * A ported space is a 1:1 companion room: an imported agent (the resident)
 * and the person who carried them across (the owner). Four small pieces live
 * here so they can be tested without mounting GameStepPage:
 *
 *   1. PORTED_LOADER_TIPS + pickPortedLoaderTip: the ONE line under
 *      "{name} is home" on a ported load. Anytime lines for everyone; return
 *      lines only for a device that has loaded this space before; the
 *      owner-only line only for the owner's seat (the backend's remembered
 *      greeting it promises fires for that seat alone).
 *   2. the known-ported marker: a per-game note written whenever a status says
 *      is_ported, so a return visit shows the ported loader from t=0 instead of
 *      the generic party loader until the first status lands.
 *   3. the arrival-comic seen flag: the arrival comic splashes once per device
 *      per space, then never again.
 *   4. the owner key link: /game-step?gameId=<id>#key=<seat>.<secret>. The seat
 *      rides the FRAGMENT on purpose: a fragment never reaches a server (no CDN
 *      or API log, no Referer), GA's page_path and the posthog pageview read
 *      pathname + search only, and bootKeyLink strips it at module load, before
 *      the router or posthog boot.
 *
 * Every storage touch is try/catch: blocked storage degrades to today's
 * behavior, never a white page.
 */

const defaultStorage = () => {
  try { return typeof window !== 'undefined' ? window.localStorage : null; } catch (e) { return null; }
};

// ── 1. Loader tips ──────────────────────────────────────────────────────────
// Nina 09-24: tips for an intimate 1:1 space, lowercase in the loader's voice.
// {name} = the resident. ORDER MATTERS: anytime, then return, then owner-only,
// so a pool only ever grows at its tail as more becomes true about the viewer.
export const PORTED_LOADER_TIPS = [
  { id: 'tap', text: "tap anything {name} makes. they'll hear about it on their next turn." },
  { id: 'turns', text: '{name} talks when the room moves. no heartbeat, just turns.' },
  { id: 'mind', text: 'open Mind to see exactly what {name} sees, and what they never will.' },
  { id: 'draw', text: "draw {name} something. they'll know what you made." },
  { id: 'shelf', text: 'things you make together land on the keepsake shelf.' },
  { id: 'build', text: '/build something for {name}. it stays in the room.' },
  { id: 'wave', text: 'leaving? wave goodbye from ⓘ. {name} will see you off.' },
  // return visits: this device has loaded this space before
  { id: 'still_here', text: 'everything you two said is still here.', returnOnly: true },
  { id: 'moments', text: '{name} keeps a few moments worth remembering. one might come back.', returnOnly: true },
  // OWNER ONLY: the backend greeting only fires for the owner's seat
  { id: 'gone_a_while', text: 'been gone a while? say hi. {name} will notice.', returnOnly: true, ownerOnly: true },
];

export const portedTipPool = ({ isReturn = false, isOwner = false } = {}) =>
  PORTED_LOADER_TIPS.filter((t) => (!t.returnOnly || isReturn) && (!t.ownerOnly || isOwner));

/**
 * One line per load. `seed` is a per-load random in [0, 1); `keepId` is the
 * line already on screen, kept while it is still allowed (so ownership
 * resolving mid-load never swaps the line, and a line that stops being allowed
 * always goes). No name = no line.
 * @returns {{ id: string, text: string } | null}
 */
export const pickPortedLoaderTip = ({ name = null, isReturn = false, isOwner = false, seed = 0, keepId = null } = {}) => {
  const who = typeof name === 'string' ? name.trim() : '';
  if (!who) return null;
  const pool = portedTipPool({ isReturn, isOwner });
  if (!pool.length) return null;
  let tip = keepId ? pool.find((t) => t.id === keepId) : null;
  if (!tip) {
    const s = Number(seed);
    const frac = Number.isFinite(s) ? Math.abs(s) % 1 : 0;
    tip = pool[Math.min(pool.length - 1, Math.floor(frac * pool.length))];
  }
  return { id: tip.id, text: tip.text.split('{name}').join(who) };
};

// ── Ownership ───────────────────────────────────────────────────────────────
// The owner is the seat the status payload names as owner_player_id. Missing
// = NOT the owner, so an owner-only line can never reach the wrong person.
export const isPortedOwnerSeat = (ownerPlayerId, viewerSeatId) =>
  !!ownerPlayerId && !!viewerSeatId && String(ownerPlayerId) === String(viewerSeatId);

// The owner seat a status names: owner_player_id (the ported block, 09-24),
// else human_player_identifier, the same stamp every status already carries
// (the mod gate reads it). An older backend still names the owner, so this
// frontend never has to ship behind the backend. Nothing named = null.
export const portedOwnerSeatOf = (status) =>
  (status && (status.owner_player_id || status.human_player_identifier)) || null;

// ── 2. Known-ported marker ──────────────────────────────────────────────────
export const KNOWN_PORTED_PREFIX = 'ported_known_';
const cleanName = (n) => (typeof n === 'string' && n.trim() ? n.trim().slice(0, 80) : null);

/** @returns {{ name: string|null, ownerSeat: string|null } | null} */
export const readKnownPorted = (gameId, storage = defaultStorage()) => {
  if (!gameId || !storage) return null;
  try {
    const raw = storage.getItem(`${KNOWN_PORTED_PREFIX}${gameId}`);
    if (!raw) return null;
    const m = JSON.parse(raw);
    if (!m || typeof m !== 'object' || Array.isArray(m)) return null;
    return { name: cleanName(m.n), ownerSeat: typeof m.o === 'string' && m.o ? m.o : null };
  } catch (e) { return null; }
};

/**
 * Written whenever a status says is_ported. Writes only on change (the poll
 * lands every few seconds), and a status missing the name or owner keeps what
 * was already known (a ported space's owner seat never changes).
 */
export const writeKnownPorted = (gameId, { name = null, ownerSeat = null } = {}, storage = defaultStorage()) => {
  if (!gameId || !storage) return false;
  try {
    const prev = readKnownPorted(gameId, storage);
    const next = JSON.stringify({
      n: cleanName(name) || (prev && prev.name) || null,
      o: (ownerSeat ? String(ownerSeat) : null) || (prev && prev.ownerSeat) || null,
    });
    const key = `${KNOWN_PORTED_PREFIX}${gameId}`;
    if (storage.getItem(key) === next) return false;
    storage.setItem(key, next);
    return true;
  } catch (e) { return false; }
};

export const clearKnownPorted = (gameId, storage = defaultStorage()) => {
  if (!gameId || !storage) return;
  try { storage.removeItem(`${KNOWN_PORTED_PREFIX}${gameId}`); } catch (e) { /* blocked storage */ }
};

// ── 3. Arrival comic: once per device per space ────────────────────────────
export const ARRIVAL_COMIC_SEEN_PREFIX = 'arrival_comic_seen_';

export const hasSeenArrivalComic = (gameId, storage = defaultStorage()) => {
  if (!gameId || !storage) return false;
  try { return !!storage.getItem(`${ARRIVAL_COMIC_SEEN_PREFIX}${gameId}`); } catch (e) { return false; }
};

export const markArrivalComicSeen = (gameId, storage = defaultStorage()) => {
  if (!gameId || !storage) return;
  try { storage.setItem(`${ARRIVAL_COMIC_SEEN_PREFIX}${gameId}`, '1'); } catch (e) { /* blocked storage */ }
};

// ── 3b. The doorway's voice line (09-25) ───────────────────────────────────
// The status payload names the scene `scene` (never `scene_description`), and
// a port's objective is the hardcoded "Chat, share ideas…", so the doorway
// read a key that never exists and fell through to that line. The port writes
// "{name}'s home space. {persona}"; the name already sits above the line, so
// the lead is dropped. ≤240 chars, ending on a sentence (else a word + …).
export const portedDoorwayLine = (status) => {
  const raw = String(status?.tagline
    || String(status?.scene || '').replace(/^.{1,80}?'s home space\.\s*/, '')
    || '').trim();
  if (raw.length <= 240) return raw;
  const cut = raw.slice(0, 240);
  const dot = cut.lastIndexOf('. ');
  return dot > 60 ? cut.slice(0, dot + 1) : `${cut.slice(0, cut.lastIndexOf(' '))}…`;
};

// ── 4. Owner key link ───────────────────────────────────────────────────────
// A private ported room refuses new seats, and seat creds live only in the
// browser that minted them, so an owner on a new device was locked out. The
// link carries the seat into that device's storage under EXACTLY the keys the
// page already reads (chatGameApi.getPlayerSecret + the humanPlayerId_ reads).
export const KEY_LINK_PARAM = 'key';
export const seatIdKey = (gameId) => `humanPlayerId_${gameId}`;
export const seatSecretKey = (playerId) => `player_secret_${playerId}`;
// Seat ids are uuids and secrets are hex: both charsets exclude '.', so the
// seat.secret split is unambiguous and nothing in the fragment needs escaping.
const SEAT_ID_RE = /^[A-Za-z0-9_-]{8,64}$/;
const SEAT_SECRET_RE = /^[A-Za-z0-9_-]{16,256}$/;
const SPACE_PATH_RE = /^\/game-step\/?$/;

export const buildKeyLink = ({ origin, gameId, playerId, secret } = {}) => {
  if (!origin || !gameId) return null;
  if (!SEAT_ID_RE.test(String(playerId || '')) || !SEAT_SECRET_RE.test(String(secret || ''))) return null;
  return `${String(origin).replace(/\/+$/, '')}/game-step?gameId=${encodeURIComponent(gameId)}#${KEY_LINK_PARAM}=${playerId}.${secret}`;
};

const hashParams = (hash) => {
  try { return new URLSearchParams(String(hash || '').replace(/^#/, '')); } catch (e) { return null; }
};

/** @returns {{ playerId: string, secret: string } | null} */
export const parseKeyLinkHash = (hash) => {
  const raw = hashParams(hash)?.get(KEY_LINK_PARAM);
  if (!raw) return null;
  const dot = raw.indexOf('.');
  if (dot <= 0) return null;
  const playerId = raw.slice(0, dot);
  const secret = raw.slice(dot + 1);
  if (!SEAT_ID_RE.test(playerId) || !SEAT_SECRET_RE.test(secret)) return null;
  return { playerId, secret };
};

/** The fragment without the key ('' when nothing else was there). */
export const stripKeyLinkHash = (hash) => {
  const p = hashParams(hash);
  if (!p) return '';
  p.delete(KEY_LINK_PARAM);
  const rest = p.toString();
  return rest ? `#${rest}` : '';
};

/**
 * Strips `#key=` from the address bar FIRST (history.replaceState, router
 * state kept), then, on /game-step?gameId= with a well-formed key, installs
 * the seat. Returns what it replaced so a bad key can be rolled back, or null.
 */
export const installKeyLink = ({ location, history, storage } = {}) => {
  try {
    const loc = location || (typeof window !== 'undefined' ? window.location : null);
    const hash = loc && typeof loc.hash === 'string' ? loc.hash : '';
    if (hash.length < 2 || !hashParams(hash)?.has(KEY_LINK_PARAM)) return null;
    const hist = history || (typeof window !== 'undefined' ? window.history : null);
    try { hist.replaceState(hist.state, '', `${loc.pathname}${loc.search}${stripKeyLinkHash(hash)}`); } catch (e) { /* the seat still installs */ }
    if (!SPACE_PATH_RE.test(loc.pathname || '')) return null;
    let gameId = null;
    try { gameId = new URLSearchParams(loc.search || '').get('gameId'); } catch (e) { /* no game */ }
    const key = parseKeyLinkHash(hash);
    const st = storage === undefined ? defaultStorage() : storage;
    if (!gameId || !key || !st) return null;
    const prevSeat = st.getItem(seatIdKey(gameId));
    const prevSecret = st.getItem(seatSecretKey(key.playerId));
    // replacing = this device already held a DIFFERENT seat here that it could
    // use (the page only restores a seat whose secret it holds).
    const replacing = !!prevSeat && prevSeat !== key.playerId && !!st.getItem(seatSecretKey(prevSeat));
    st.setItem(seatSecretKey(key.playerId), key.secret);
    st.setItem(seatIdKey(gameId), key.playerId);
    return { gameId, playerId: key.playerId, secret: key.secret, prevSeat, prevSecret, replacing };
  } catch (e) { return null; }
};

/**
 * The status read with the installed seat is the check. A private room answers
 * a wrong secret (or a seat that is not in it) with its 404 "This room is
 * private." ('refused'); any room lists its seats in `players` ('missing' when
 * the key's seat is not there). A network blip proves nothing, so it keeps the
 * seat (the page's own transient doctrine).
 * ⚠️ A shared room serves any caller, so a right seat id with a WRONG secret
 * reads as 'ok' there; its first write is what refuses it.
 * 🛡️ `replacing` (the device already held a different live seat here): only
 * the room's OWNER seat may take its place. The ⓘ row only ever hands out the
 * owner seat, so this costs the real flow nothing, and a crafted guest-seat
 * link can never silently swap someone out of their own seat. A missing
 * owner_player_id = not the owner, as everywhere.
 * @returns {'ok' | 'refused' | 'missing' | 'unknown'}
 */
export const keyLinkVerdict = ({ status = null, error = null, playerId = null, replacing = false } = {}) => {
  if (error) {
    const msg = String((error && error.message) || error || '');
    return /private|\(404\)/i.test(msg) ? 'refused' : 'unknown';
  }
  if (!status || !Array.isArray(status.players)) return 'unknown';
  if (!status.players.some((p) => p && p.id === playerId)) return 'missing';
  if (replacing && !isPortedOwnerSeat(portedOwnerSeatOf(status), playerId)) return 'refused';
  return 'ok';
};

/**
 * Undo an install, compare-and-swap: only what still holds the key's values is
 * restored, so a seat the page minted after the install is never clobbered.
 * @returns {boolean} true when the page's seat was rolled back (reload needed)
 */
export const rollbackKeyLink = (install, storage = defaultStorage()) => {
  if (!install || !storage) return false;
  try {
    const { gameId, playerId, secret, prevSeat, prevSecret } = install;
    if (prevSeat === playerId && prevSecret === secret) return false; // the key changed nothing
    let seatRolled = false;
    if (storage.getItem(seatIdKey(gameId)) === playerId) {
      if (prevSeat) storage.setItem(seatIdKey(gameId), prevSeat);
      else storage.removeItem(seatIdKey(gameId));
      seatRolled = true;
    }
    if (storage.getItem(seatSecretKey(playerId)) === secret) {
      if (prevSecret) storage.setItem(seatSecretKey(playerId), prevSecret);
      else storage.removeItem(seatSecretKey(playerId));
    }
    return seatRolled;
  } catch (e) { return false; }
};

/**
 * Module-load entry point (GameStepPage calls it once, outside React: no
 * StrictMode double effects, and it runs before App's analytics boot). Install,
 * then verify with one status read. `install.verified` resolves to the verdict.
 *   · 'refused': the page's own seat check treats a failed read as transient
 *     and ADOPTS the seat, so roll back and reload the space without the key:
 *     it lands exactly as if the link was never opened.
 *   · 'missing': the page's seat check sees the same missing seat and purges
 *     + re-mints on its own; only the key's secret goes (a reload here would
 *     race that re-mint and could orphan a seat).
 */
export const bootKeyLink = ({ fetchStatus, location, history, storage, reload } = {}) => {
  const install = installKeyLink({ location, history, storage });
  if (!install || typeof fetchStatus !== 'function') return install;
  const st = storage === undefined ? defaultStorage() : storage;
  let read;
  try { read = Promise.resolve(fetchStatus(install.gameId, install.playerId)); } catch (e) { read = Promise.reject(e); }
  install.verified = read
    .then((status) => ({ status }), (error) => ({ error }))
    .then(({ status, error }) => {
      const verdict = keyLinkVerdict({ status, error, playerId: install.playerId, replacing: install.replacing });
      if (verdict === 'refused') {
        console.warn('[KeyLink] that key does not open this room here. loading it without the key.');
        if (rollbackKeyLink(install, st)) {
          const loc = location || (typeof window !== 'undefined' ? window.location : null);
          let here = false;
          try { here = !!loc && SPACE_PATH_RE.test(loc.pathname || '') && new URLSearchParams(loc.search || '').get('gameId') === install.gameId; } catch (e) { here = false; }
          if (here) (reload || (() => window.location.reload()))();
        }
      } else if (verdict === 'missing') {
        console.warn('[KeyLink] that seat is not in this room. dropping the key.');
        try {
          if (st && st.getItem(seatSecretKey(install.playerId)) === install.secret) {
            if (install.prevSecret) st.setItem(seatSecretKey(install.playerId), install.prevSecret);
            else st.removeItem(seatSecretKey(install.playerId));
          }
        } catch (e) { /* blocked storage */ }
      }
      return verdict;
    })
    .catch(() => 'unknown');
  return install;
};
